import {
  BMButton,
  BMComponentsType,
  BMElevationType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { useTravelerPickerContextV2 } from "../../../../providers/TravelerPickerContextV2";
import { formatMessage } from "../../../../utils";
import { FormValues } from "./types";

const DEFAULT_NON_EMPLOYEE = {
  adults: 0,
  children: 0,
  infants: 0,
  childrenAges: [],
};

export default function TravelerPickerFooter() {
  const { type, onClose, onSubmit } = useTravelerPickerContextV2();
  const { CorporateTravelerPicker: cr } = useMobileContentResource();

  const { BMComponents, BMElevation } = useBMTheme();
  const styles = createStyles(BMComponents, BMElevation);

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useFormContext<FormValues<any>>();
  const [employees = [], nonEmployee = DEFAULT_NON_EMPLOYEE] = useWatch({
    control,
    name: ["employees", "nonEmployee"],
  });

  const { hasEmployee, subtitleText } = useMemo(() => {
    const numOfAdults = nonEmployee.adults ?? 0;
    const numOfChildren = nonEmployee.children ?? 0;
    const numOfInfants = nonEmployee.infants ?? 0;
    const numOfChildrenAges = nonEmployee.childrenAges?.length ?? 0;
    const hasEmployee =
      employees.length +
        numOfAdults +
        numOfChildren +
        numOfInfants +
        numOfChildrenAges >
      0;

    let subtitleText: string | undefined = undefined;
    if (hasEmployee && type === "flight") {
      subtitleText = [
        formatMessage(cr.adultUnit.toLowerCase(), {
          num: employees.length + numOfAdults,
        }),
        formatMessage(cr.childUnit.toLowerCase(), {
          num: numOfChildren,
        }),
        formatMessage(cr.infantUnit.toLowerCase(), {
          num: numOfInfants,
        }),
      ].join(", ");
    } else if (hasEmployee && type === "hotel") {
      subtitleText = [
        formatMessage(cr.adultUnit.toLowerCase(), {
          num: employees.length + numOfAdults,
        }),
        formatMessage(cr.childUnit.toLowerCase(), {
          num: numOfChildrenAges,
        }),
      ].join(", ");
    }

    return {
      hasEmployee,
      subtitleText,
    };
  }, [employees, nonEmployee]);

  return (
    <BMView style={styles.footer} row justify="between" align="center">
      <BMView style={styles.footerLabel} justify="end" spacing="sm">
        <BMText variant="titleSmall">
          {hasEmployee
            ? formatMessage(cr.selectedTravelerFooterText, { type })
            : formatMessage(cr.emptyTravelerFooterText, { type })}
        </BMText>
        {subtitleText && (
          <BMText variant="uiNormal" ink="BMStyleTextInkSecondary">
            {subtitleText}
          </BMText>
        )}
      </BMView>
      <BMButton
        label={cr.submitButtonText}
        onPress={() => {
          handleSubmit(onSubmit)();
          onClose();
        }}
        disabled={!isDirty}
        size="LARGE"
        testID="traveler_picker-submit"
      />
    </BMView>
  );
}

function createStyles(
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType
) {
  return StyleSheet.create({
    footer: {
      paddingHorizontal: BMPrimitives.BMSize.md,
      paddingVertical: BMPrimitives.BMSize.lg,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      ...BMElevation.elevation2,
    },
    footerLabel: {
      maxWidth: 215,
    },
  });
}
